import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { type MaterialGradePayload } from './types';
import {
  TEMPLATE_HEADERS,
  MG_SAMPLE_ROW,
  validateMGHeaders,
} from './materialGradeUtils';

interface ParsedRow {
  id: string;
  rowNum: number;
  make: string;
  model: string;
  materialCategory: string;
  tonnage: number | null;
  volume: number | null;
  errors: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImport: (entries: MaterialGradePayload[]) => Promise<void>;
}

const downloadTemplate = () => {
  const sampleValues = TEMPLATE_HEADERS.map((h) => {
    const v = MG_SAMPLE_ROW[h] ?? '';
    return v.includes(',') ? `"${v}"` : v;
  });
  const csv = [TEMPLATE_HEADERS.join(','), sampleValues.join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'material_grade_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const downloadErrors = (errorRows: ParsedRow[]) => {
  const rows = errorRows.map((r) => ({
    MAKE: r.make,
    MODEL: r.model,
    MATERIAL_CATEGORY: r.materialCategory,
    TONNAGE: r.tonnage ?? '',
    VOLUME: r.volume ?? '',
    ERRORS: r.errors.join('; '),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Errors');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `material_grade_errors_${Date.now()}.xlsx`
  );
};

const parseRaw = (raw: Array<Record<string, string>>): ParsedRow[] =>
  raw.map((row, idx) => {
    const errors: string[] = [];
    const make = (row.MAKE ?? '').trim();
    const model = (row.MODEL ?? '').trim();
    const materialCategory = (row.MATERIAL_CATEGORY ?? '').trim();
    const tonnageRaw = (row.TONNAGE ?? '').trim();
    const volumeRaw = (row.VOLUME ?? '').trim();

    if (!make) errors.push('MAKE is required');
    if (!model) errors.push('MODEL is required');
    if (!materialCategory) errors.push('MATERIAL_CATEGORY is required');
    const tonnage = parseFloat(tonnageRaw);
    if (isNaN(tonnage) || tonnage <= 0)
      errors.push('TONNAGE must be a positive number');
    const volume = parseFloat(volumeRaw);
    if (isNaN(volume) || volume <= 0)
      errors.push('VOLUME must be a positive number');

    return {
      id: crypto.randomUUID(),
      rowNum: idx + 2,
      make,
      model,
      materialCategory,
      tonnage: isNaN(tonnage) ? null : tonnage,
      volume: isNaN(volume) ? null : volume,
      errors,
    };
  });

export const ImportMaterialGradeModal = ({
  open,
  onClose,
  onImport,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [validRows, setValidRows] = useState<ParsedRow[]>([]);
  const [errorRows, setErrorRows] = useState<ParsedRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setValidRows([]);
    setErrorRows([]);
    setSelected(new Set());
    setHeaderError(null);
    setFileName(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const processRows = useCallback((rawRows: Array<Record<string, string>>) => {
    const parsed = parseRaw(rawRows);
    const valid = parsed.filter((r) => r.errors.length === 0);
    const invalid = parsed.filter((r) => r.errors.length > 0);
    setValidRows(valid);
    setErrorRows(invalid);
    setSelected(new Set(valid.map((r) => r.id)));
  }, []);

  const processFile = useCallback(
    (file: File) => {
      setFileName(file.name);
      setHeaderError(null);

      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

      if (ext === 'csv') {
        Papa.parse<Record<string, string>>(file, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const headers = result.meta.fields ?? [];
            const { valid, missing } = validateMGHeaders(headers);
            if (!valid) {
              setHeaderError(
                t('mg_import_missing_cols', { cols: missing.join(', ') })
              );
              return;
            }
            processRows(result.data);
          },
        });
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = e.target?.result;
          if (!data) return;
          const wb = XLSX.read(data, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json<Record<string, string>>(ws, {
            defval: '',
          });
          const headers = json.length > 0 ? Object.keys(json[0]) : [];
          const { valid, missing } = validateMGHeaders(headers);
          if (!valid) {
            setHeaderError(
              t('mg_import_missing_cols', { cols: missing.join(', ') })
            );
            return;
          }
          processRows(json);
        };
        reader.readAsArrayBuffer(file);
      }
    },
    [processRows, t]
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === validRows.length
        ? new Set()
        : new Set(validRows.map((r) => r.id))
    );
  };

  const handleImport = async () => {
    const toImport = validRows
      .filter((r) => selected.has(r.id))
      .map<MaterialGradePayload>((r) => ({
        make: r.make,
        model: r.model,
        materialCategory: r.materialCategory,
        tonnage: r.tonnage ?? 0,
        volume: r.volume ?? 0,
      }));
    if (toImport.length === 0) return;
    setSubmitting(true);
    try {
      await onImport(toImport);
    } catch {
      // toasted by parent
    } finally {
      setSubmitting(false);
    }
  };

  const hasFile = fileName !== null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.25),
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('mg_import_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('mg_import_subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
            onClick={downloadTemplate}
            variant="outlined"
            sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600 }}
          >
            {t('mg_import_download_template')}
          </Button>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2, overflowY: 'auto', flex: 1 }}>
        {/* Drop zone */}
        {!hasFile && (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => {
              setDragOver(false);
            }}
            onDrop={handleDrop}
            component="label"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'stretch',
              border: '1.5px dashed',
              borderColor: dragOver
                ? 'primary.main'
                : (th) => alpha(th.palette.primary.main, 0.35),
              borderRadius: 3,
              py: 4,
              px: 3,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.18s',
              bgcolor: dragOver
                ? (th) => alpha(th.palette.primary.main, 0.06)
                : (th) => alpha(th.palette.primary.main, 0.03),
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.06),
              },
            }}
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.3),
                mb: 1.5,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 26, color: 'primary.main' }} />
            </Box>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {t('mg_import_drop_hint')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {t('mg_import_accepts')}
            </Typography>
          </Box>
        )}

        {/* File loaded indicator */}
        {hasFile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 16, color: 'text.secondary' }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexGrow: 1 }}
            >
              {fileName}
            </Typography>
            <Button
              size="small"
              onClick={reset}
              variant="text"
              color="inherit"
              sx={{ fontSize: '0.72rem' }}
            >
              {t('mg_import_change_file')}
            </Button>
          </Box>
        )}

        {/* Header error */}
        {headerError && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.error.main, 0.08),
              mb: 2,
            }}
          >
            <Typography variant="body2" color="error.main">
              {headerError}
            </Typography>
          </Box>
        )}

        {/* Error rows */}
        {errorRows.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" color="error.main">
                {t('mg_import_error_rows', { count: errorRows.length })}
              </Typography>
              <Button
                size="small"
                startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                onClick={() => {
                  downloadErrors(errorRows);
                }}
                variant="outlined"
                color="error"
                sx={{ borderRadius: '8px', fontSize: '0.72rem' }}
              >
                {t('mg_import_download_errors')}
              </Button>
            </Box>
            <TableContainer
              sx={{
                maxHeight: 160,
                borderRadius: 2,
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.error.main, 0.2),
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mg_import_col_row')}</TableCell>
                    <TableCell>{t('mg_import_col_make')}</TableCell>
                    <TableCell>{t('mg_import_col_model')}</TableCell>
                    <TableCell>{t('mg_import_col_errors')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.rowNum}</TableCell>
                      <TableCell>{r.make}</TableCell>
                      <TableCell>{r.model}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="error.main">
                          {r.errors.join('; ')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Valid rows */}
        {validRows.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.primary">
                {t('mg_import_valid_rows', { count: validRows.length })}
              </Typography>
              <Chip
                label={t('mg_import_selected', { count: selected.size })}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <TableContainer
              sx={{
                maxHeight: 300,
                borderRadius: 2,
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.divider, 0.3),
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={
                          selected.size > 0 && selected.size < validRows.length
                        }
                        checked={
                          selected.size === validRows.length &&
                          validRows.length > 0
                        }
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell>{t('mg_col_make')}</TableCell>
                    <TableCell>{t('mg_col_model')}</TableCell>
                    <TableCell>{t('mg_col_material_category')}</TableCell>
                    <TableCell align="right">{t('mg_col_tonnage')}</TableCell>
                    <TableCell align="right">{t('mg_col_volume')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validRows.map((r) => (
                    <TableRow key={r.id} hover selected={selected.has(r.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(r.id)}
                          onChange={() => {
                            toggleRow(r.id);
                          }}
                        />
                      </TableCell>
                      <TableCell>{r.make}</TableCell>
                      <TableCell>{r.model}</TableCell>
                      <TableCell>{r.materialCategory}</TableCell>
                      <TableCell align="right">{r.tonnage}</TableCell>
                      <TableCell align="right">{r.volume}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={submitting}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('mg_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleImport();
          }}
          disabled={submitting || selected.size === 0}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('mg_import_btn_submit', { count: selected.size })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
